<template>
  <div class="card-body">
    <LoadingContainer v-if="loading" />

    <EmptyState
      v-else-if="!connectAccounts || connectAccounts.length === 0"
      :msg="'Nenhuma conta Stripe Connect encontrada'"
      :title="'Sem contas'"
    />

    <div v-else>
      <div class="mb-3 text-end">
        <button
          class="btn btn-outline-danger btn-sm"
          @click="showResetModal = true"
        >
          <i class="fa-solid fa-rotate-left me-1"></i> Resetar conta Stripe
        </button>
      </div>

      <div class="row">
        <div class="col-12">
          <div
            v-for="(account, index) in connectAccounts"
            :key="'connect-' + index"
            class="card mb-3"
          >
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ account.stripe_id }}</strong>
                <span class="badge bg-info ms-2">{{
                  getBusinessTypeLabel(account.business_type)
                }}</span>
              </div>
              <a
                v-if="account.stripe_id"
                :href="getStripeLink(account.stripe_id)"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fa-brands fa-stripe"></i> Ver no Stripe
              </a>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-12">
                  <h6 class="text-muted mb-3">Informações da Conta</h6>
                  <table class="table table-sm table-borderless">
                    <tbody>
                      <tr>
                        <td class="text-muted" style="width: 150px">Nome:</td>
                        <td>
                          <strong>{{ getAccountName(account) }}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Cobranças:</td>
                        <td>
                          <span :class="getStatusClass(account.charges_enabled)">
                            {{
                              account.charges_enabled
                                ? "Habilitado"
                                : "Desabilitado"
                            }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Repasses:</td>
                        <td>
                          <span :class="getStatusClass(account.payouts_enabled)">
                            {{
                              account.payouts_enabled
                                ? "Habilitado"
                                : "Desabilitado"
                            }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Pendências:</td>
                        <td>
                          <span
                            v-if="
                              account.currently_due &&
                              account.currently_due.length > 0
                            "
                            class="badge bg-warning"
                          >
                            {{ account.currently_due.length }} item(s)
                          </span>
                          <span v-else class="badge bg-success">Nenhuma</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Criado em:</td>
                        <td>{{ account.created_at_formatted }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div
                v-if="account.currently_due && account.currently_due.length > 0"
                class="alert alert-warning mt-3"
              >
                <strong
                  ><i class="fa-solid fa-triangle-exclamation"></i>
                  Pendências:</strong
                >
                <ul class="mb-0 mt-2">
                  <li
                    v-for="(item, idx) in account.currently_due"
                    :key="'due-' + idx"
                  >
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Reset Stripe -->
    <div
      class="modal fade"
      :class="{ show: showResetModal }"
      :style="{ display: showResetModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">
              <i class="fa-solid fa-triangle-exclamation me-2"></i>
              Resetar conta Stripe Connect
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="closeResetModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <strong>Atenção!</strong> Esta ação irá:
            </div>
            <ul class="mb-3">
              <li>A conta do PsiApp do psicólogo <strong>continua normal</strong></li>
              <li>Somente o cadastro na <strong>plataforma de pagamento</strong> (Stripe) será resetado</li>
              <li>O psicólogo será <strong>deslogado</strong> do aplicativo</li>
              <li>Será necessário pedir para ele <strong>logar novamente</strong> com o mesmo email e senha</li>
              <li>Ao logar, ele precisará criar um <strong>novo cadastro</strong> na plataforma de pagamento</li>
            </ul>
            <div class="form-check mt-4">
              <input
                class="form-check-input"
                type="checkbox"
                id="confirmResetCheckbox"
                v-model="confirmReset"
              />
              <label class="form-check-label" for="confirmResetCheckbox">
                <strong>Eu li e entendi</strong> as consequências desta ação
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeResetModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="!confirmReset || resetting"
              @click="handleResetStripe"
            >
              <span v-if="resetting" class="spinner-border spinner-border-sm me-1"></span>
              Confirmar Reset
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showResetModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
export default {
  name: "PsiDetailConnectAccounts",
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      connectAccounts: [],
      showResetModal: false,
      confirmReset: false,
      resetting: false,
    };
  },
  methods: {
    async loadConnectAccounts() {
      if (!this.item?.id) return;

      this.loading = true;
      try {
        const response = await this.$connectAccountService.list({
          user_id: this.item.id,
        });

        this.connectAccounts = response.data.map((account) => {
          let currentlyDue = account.currently_due_ptbr;
          if (typeof currentlyDue === 'string' && currentlyDue.length > 0) {
            currentlyDue = currentlyDue.split('|');
          } else if (!Array.isArray(currentlyDue)) {
            currentlyDue = [];
          }

          return {
            ...account,
            currently_due: currentlyDue,
            created_at_formatted: account.created_at
              ? this.$moment(account.created_at).format("DD/MM/YYYY HH:mm")
              : "-",
          };
        });
      } catch (error) {
        console.error("Erro ao carregar contas Stripe Connect:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao carregar contas Stripe Connect",
        });
      } finally {
        this.loading = false;
      }
    },
    getStatusClass(enabled) {
      return enabled ? "badge bg-success" : "badge bg-secondary";
    },
    getStripeLink(accountId) {
      const baseUrl =
        process.env.VUE_APP_ENV === "prod"
          ? "https://dashboard.stripe.com"
          : "https://dashboard.stripe.com/test";
      return `${baseUrl}/connect/accounts/${accountId}`;
    },
    getBusinessTypeLabel(type) {
      if (type === "individual") return "Pessoa Física";
      if (type === "company") return "Empresa";
      return type;
    },
    getAccountName(account) {
      if (account.business_type === "individual") {
        return (
          `${account.individual_first_name || ""} ${
            account.individual_last_name || ""
          }`.trim() || "-"
        );
      }
      if (account.business_type === "company") {
        return account.company_name || "-";
      }
      return "-";
    },
    closeResetModal() {
      this.showResetModal = false;
      this.confirmReset = false;
    },
    async handleResetStripe() {
      if (!this.item?.id) return;

      this.resetting = true;
      try {
        await this.$userService.network.post(
          `api/users/stripe-connect-reset/${this.item.id}`
        );

        this.$eventBus.$emit("showToast", {
          title: "Sucesso",
          msg: "Conta Stripe Connect resetada com sucesso. O psicólogo foi deslogado.",
        });

        this.closeResetModal();
        this.loadConnectAccounts();
      } catch (error) {
        console.error("Erro ao resetar conta Stripe:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao resetar conta Stripe Connect",
        });
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>
